import { CartAddEvent } from '@theme/events';

/**
 * Carousel dots for the cart drawer upsell ("S-ar putea să-ți placă").
 * The track scrolls natively (scroll-snap); the dots are server-rendered in
 * Liquid (client-generated DOM would be wiped by cart morphing) — this element
 * only syncs the active dot and handles dot clicks. Re-connects automatically
 * when the drawer content is morphed after cart updates.
 */
class CartUpsellSlider extends HTMLElement {
  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true });
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    this.dotsContainer?.removeEventListener('click', this.#onDotClick);
    this.track?.removeEventListener('scroll', this.#onScroll);
  }

  #init() {
    if (!this.isConnected) return;
    this.#teardown();
    this.track = this.querySelector('.cart-upsell__track');
    this.dotsContainer = this.querySelector('.cart-upsell__dots');
    if (!this.track || !this.dotsContainer) return;
    this.cards = Array.from(this.track.querySelectorAll('.cart-upsell__card'));

    this.dotsContainer.addEventListener('click', this.#onDotClick);
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  #onDotClick = (event) => {
    const dot = event.target.closest('.cart-upsell__dot');
    if (!dot) return;
    const card = this.cards[Number(dot.dataset.index)];
    if (!card) return;
    this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'smooth' });
  };

  #onScroll = () => {
    this.#update();
  };

  #update() {
    if (!this.cards?.length) return;
    const scrollLeft = this.track.scrollLeft;
    const firstLeft = this.cards[0].offsetLeft;
    let activeIndex = 0;
    let best = Infinity;
    this.cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - firstLeft - scrollLeft);
      if (distance < best) {
        best = distance;
        activeIndex = i;
      }
    });
    this.dotsContainer.querySelectorAll('.cart-upsell__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === activeIndex);
    });
  }
}

if (!customElements.get('cart-upsell-slider')) {
  customElements.define('cart-upsell-slider', CartUpsellSlider);
}

/**
 * AJAX add-to-cart for the upsell cards: stay on the page and refresh the
 * drawer via CartAddEvent instead of the form's native POST to /cart/add
 * (which navigates to the cart page). Delegated on document so it survives
 * the drawer's innerHTML replacement after every cart update.
 */
async function addUpsellToCart(form) {
  const button = form.querySelector('.cart-upsell__atc-btn');
  if (button) {
    button.disabled = true;
    // Spinner stays until the drawer refresh replaces this DOM node.
    button.classList.add('is-loading');
  }

  try {
    const formData = new FormData(form);

    // Ask Shopify to render the cart sections in the same request so the
    // drawer can morph without an extra round-trip (same as product-form.js).
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const result = await response.json();
    if (result.status) throw new Error(result.message || 'Add to cart failed');

    const cart = await fetch('/cart.js').then((r) => r.json());

    form.dispatchEvent(
      new CartAddEvent(cart, 'cart-upsell', {
        source: 'cart-upsell',
        itemCount: 1,
        variantId: String(formData.get('id')),
        sections: result.sections,
      })
    );
  } catch (error) {
    console.error('[cart-upsell]', error);
    if (button) {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-upsell__form')) return;
  event.preventDefault();
  addUpsellToCart(form);
});
